import {
  OnDestroy,
  AfterViewInit,
  NgZone,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  output,
  viewChildren
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';

import { Subject, merge, Observable, defer } from 'rxjs';
import { takeUntil, take, switchMap } from 'rxjs/operators';

import { FlyoutType, MenuPanelItem } from '../../domain/menu.domain';
import { MenuItemComponent } from './item/item.component';

/**
 * The Menu Flyout Component
 *
 * This is the wrapper for flyout contents
 */
@Component({
  template: ''
})
export abstract class MenuNavigationBaseComponent implements OnDestroy, AfterViewInit {

  /**
   * Prefix for flyout class
   */
  private static readonly FLYOUT_CLASS_PREFIX: string = 'td-wb-nav__';

  /**
   * Flyout type
   */
  readonly flyoutType = input<FlyoutType>(undefined);

  readonly selected = output<void>();

  readonly menuItemComponents = viewChildren(MenuItemComponent);

  private open: boolean = false;
  private readonly destroy: Subject<void> = new Subject<void>();
  private readonly menuItemSelected: Observable<MenuPanelItem>  = defer((): Observable<MenuPanelItem> => {
    const menuItemComponents = this.menuItemComponents();
    if (menuItemComponents !== undefined && menuItemComponents !== null) {
      return merge(
        ...menuItemComponents.map((item: MenuItemComponent): Observable<MenuPanelItem> => outputToObservable(item.selected))
      );
    }
    return this.ngZone.onStable
      .asObservable()
      .pipe(take(1), switchMap((): Observable<MenuPanelItem> => this.menuItemSelected));
  });

  private readonly ngZone = inject(NgZone);
  protected readonly changeDetectorRef = inject(ChangeDetectorRef);

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  ngAfterViewInit(): void {
    // Note: viewChildren() is a signal; we subscribe once to initialize state
    this.resetItems();
    this.open = this.menuItemComponents().some((component: MenuItemComponent): boolean => {
      const item = component.menuItem();
      return item !== undefined && item.open && item.menu !== undefined && item.menu[0].item !== undefined;
    });
  }

  /**
   * This function returns flyout class
   *
   * @returns Returns the class for flyout.
   */
  getFlyoutClass(): string {
    return MenuNavigationBaseComponent.FLYOUT_CLASS_PREFIX + this.flyoutType();
  }

  close(): void {
    this.menuItemComponents().forEach((menuItemComponent: MenuItemComponent): void => {
      menuItemComponent.close();
    });
    this.open = false;
    this.changeDetectorRef.detectChanges();
  }

  isOpen(): boolean {
    return this.open;
  }

  private resetItems(): void {
    const changedOrDestroyed = this.destroy;

    this.menuItemSelected
      .pipe(
        takeUntil(changedOrDestroyed)
      )
      .subscribe((item: MenuPanelItem): void => {
        this.menuItemComponents().forEach((menuItemComponent: MenuItemComponent): void => {
          const menuItem = menuItemComponent.menuItem();
          if (menuItem !== undefined &&
            menuItem.id !== item.id &&
            menuItemComponent.isOpen()) {
            menuItemComponent.close();
          }
        });
        this.open = item.open && item.menu !== undefined && item.menu[0].item !== undefined;
        this.selected.emit(undefined);
        this.changeDetectorRef.detectChanges();
      });
  }
}
